import { Injectable } from '@angular/core';
import { ModalComponent } from '~shared/components/modal/modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private modals: ModalComponent[] = [];

  add(modal: ModalComponent) {
    // ensure component has a unique id attribute
    if (!modal.id || this.modals.find((x) => x.id === modal.id)) {
      throw new Error('modal must have a unique id attribute');
    }
    // il y aura sans doute des choses à faire plus tard ici, si on a plusieurs modals dans une seule page.
    // add modal to array of active modals
    this.modals.push(modal);
  }

  remove(modal: ModalComponent) {
    // remove modal from array of active modals
    this.modals = this.modals.filter((x) => x !== modal);
  }

  open(id: string) {
    // open modal specified by id
    const modal = this.modals.find((x) => x.id === id);

    if (!modal) {
      throw new Error(`modal '${id}' not found`);
    }

    modal.open();
  }

  close() {
    // close the modal that is currently open
    const modal = this.modals.find((x) => x.isOpen);
    modal?.close();
  }

  /**
   * Les appuis d'Échap déjà honorés.
   *
   * `WeakSet` plutôt qu'un marqueur pose sur l'événement : on ne décore pas
   * un objet du DOM qui ne nous appartient pas, et l'entrée disparaît avec
   * l'événement.
   */
  private readonly echapsHonores = new WeakSet<Event>();

  /**
   * Cette modale doit-elle répondre à CET appui d'Échap ?
   *
   * Chaque modale ecoute `document:keydown.escape` : un seul appui declenche
   * donc TOUS les ecouteurs, l'un apres l'autre. Interroger `estAuPremierPlan`
   * dans chacun ne suffit pas, parce que l'etat change entre deux : la
   * premiere modale se ferme, et la suivante devient alors « la derniere
   * ouverte » -- un seul appui en fermait deux.
   *
   * Mesure avant correction, deux modales ouvertes dont la premiere
   * enregistree tient le focus : `{ a: false, b: false }`.
   *
   * La decision est donc prise UNE FOIS par appui, et retenue.
   */
  reclamerEchap(modal: ModalComponent, evenement?: Event): boolean {
    if (evenement && this.echapsHonores.has(evenement)) {
      return false;
    }
    if (!this.estAuPremierPlan(modal)) {
      return false;
    }
    if (evenement) {
      this.echapsHonores.add(evenement);
    }
    return true;
  }

  /**
   * La modale qui doit répondre à Échap, parmi celles qui sont ouvertes.
   *
   * Chaque modale écoute `document:keydown.escape` : sans arbitre, deux
   * modales ouvertes se fermeraient toutes les deux d'un seul appui.
   *
   * Celle qui TIENT LE FOCUS a la priorité — c'est celle que l'utilisateur
   * voit et dans laquelle il navigue, puisque `DialogFocusService.capture()`
   * y a posé le focus à l'ouverture. À défaut (le focus a pu retomber sur
   * `body`, par exemple après un clic sur le fond), on prend la dernière
   * ouverte : c'est celle du dessus.
   */
  estAuPremierPlan(modal: ModalComponent): boolean {
    const ouvertes = this.modals.filter((x) => x.isOpen);
    if (ouvertes.length === 0) {
      return false;
    }
    const actif = document.activeElement;
    const tenantLeFocus = actif
      ? ouvertes.find((x) => x.contient(actif))
      : undefined;
    return (tenantLeFocus ?? ouvertes[ouvertes.length - 1]) === modal;
  }
}
